<!-- Toast Messages - Positioned at root level -->
<p-toast
  data-testid="synchronization-toast"
  position="top-right"
  [baseZIndex]="9999"
  [autoZIndex]="true"
  [showTransformOptions]="'translateX(100%)'"
  [hideTransformOptions]="'translateX(100%)'"
  [showTransitionOptions]="'0.3s ease-in'"
  [hideTransitionOptions]="'0.3s ease-out'"
  [preventDuplicates]="false"
></p-toast>

<div data-testid="synchronization-page" class="p-6 flex-table-page">
  <!-- Header -->
  <h2 class="text-2xl font-semibold text-gray-800 mb-3 flex-table-header">
    {{ "synchronization.management.title" | translate }}
  </h2>

  <app-search-form
    data-testid="synchronization-search-form"
    [searchFormat]="searchConfig"
    (formSearch)="onSearch($event)"
    (formReset)="onReset()"
  >
    <div slot="actions" class="flex gap-2">
      <app-button
        [testId]="'synchronization-column-config-btn'"
        type="success"
        [outlined]="true"
        icon="view_column"
        (emitOnClick)="onColumnConfigClick($event)"
      ></app-button>
      <app-status-filter
        [primaryButton]="primaryButton()"
        [secondaryButton]="secondaryButton()"
        (primaryButtonClick)="onAdd()"
        (secondaryButtonClick)="onBatchDelete()"
      ></app-status-filter>
    </div>
  </app-search-form>

  <!-- Data Table -->
  <div class="flex-table-section">
    <advanced-data-table
      data-testid="synchronization-table"
      #advancedTable
      [dataSource]="synchronizations()"
      [defaultColumns]="defaultColumns()"
      [flexibleColumns]="flexibleColumns()"
      [loading]="loading()"
      [totalRecords]="totalRecords()"
      [rows]="searchParams().size"
      [first]="(searchParams().page - 1) * searchParams().size"
      [hasCheckColumn]="canDelete()"
      [selection]="selectedSynchronizations()"
      [showInternalColumnConfigButton]="false"
      scrollHeight="flex"
      (selectionChange)="onSelectionChange($event)"
      (pageChange)="onPageChange($event)"
    >
    <!-- Custom Status Template -->
    <ng-template #statusTemplate let-status>
      <div class="flex items-center justify-center gap-1">
        <p-tag
          data-testid="synchronization-status-tag"
          [value]="getStatusDisplayText(status)"
          [severity]="getStatusSeverity(status)"
          styleClass="status-tag"
          [ngStyle]="getStatusStyle(status)"
          [pTooltip]="getStatusDisplayText(status)"
          tooltipPosition="top"
          tooltipStyleClass="custom-tooltip"
          [showDelay]="300"
          [hideDelay]="0"
        />
      </div>
    </ng-template>

    <!-- Custom Template for Operation Time and Creation Date -->
    <ng-template
      #customTemplate
      let-value
      let-column="column"
      let-rowData="rowData"
    >
      @if (column.prop === "lastUpdateDate" || column.prop === "creationDate") {
        <span class="text-xs text-gray-600 flex items-center justify-center">
          {{ formatDateTime(value) }}
        </span>
      } @else {
        {{ value }}
      }
    </ng-template>

    <!-- Custom Actions Template -->
    <ng-template #actionsTemplate let-rowData>
      <div class="flex gap-2 justify-center">
        <!-- Edit/View Action - Show Edit for 'wait' status, View for others -->
        @if (rowData.status === "wait" && canUpdate()) {
          <app-button
            [testId]="'synchronization-action-edit-' + rowData.id"
            class="action-edit"
            [label]="'synchronization.actions.edit' | translate"
            type="action"
            (emitOnClick)="onEdit(rowData)"
          ></app-button>
        } @else if (rowData.status !== "wait" && canRead()) {
          <app-button
            [testId]="'synchronization-action-view-' + rowData.id"
            class="action-view"
            [label]="'common.actions.view' | translate"
            type="action"
            (emitOnClick)="onView(rowData)"
          ></app-button>
        }

        <!-- Review Action - Only for 'wait' or 'pending' status -->
        @if (
          (rowData.status === "wait" || rowData.status === "pending") &&
          !approvalDetailsLoading &&
          canApprove()
        ) {
          <app-button
            [testId]="'synchronization-action-review-' + rowData.id"
            class="action-approve"
            [label]="'synchronization.actions.review' | translate"
            type="action"
            (emitOnClick)="onReview(rowData)"
          ></app-button>
        }

        <!-- Delete Action - Only for non-wait status -->
        @if (rowData.status !== "wait" && canDelete()) {
          <app-button
            [testId]="'synchronization-action-delete-' + rowData.id"
            class="action-delete"
            [label]="'synchronization.actions.delete' | translate"
            type="action"
            (emitOnClick)="onDelete(rowData)"
          ></app-button>
        }
      </div>
    </ng-template>
    </advanced-data-table>
  </div>
</div>

<!-- Shared Confirmation Dialog -->
<app-confirmation-dialog
  [testId]="'synchronization-confirmation-dialog'"
  [(visible)]="showConfirmationDialog"
  [title]="dialogConfig()?.title || ''"
  [message]="dialogConfig()?.message || ''"
  [buttons]="dialogConfig()?.buttons || []"
  (emitOnClick)="onConfirmatiButtonClick($event)"
></app-confirmation-dialog>
<!-- Approval Details Dialog -->
<app-approval-details-dialog
  data-testid="synchronization-approval-details-dialog"
  [visible]="showApprovalDetailsDialog"
  [config]="approvalDetailsConfig"
  [adjustmentDetails]="approvalDetailsData?.adjustDetails || []"
  [loading]="approvalDetailsLoading"
  [synchronizationData]="approvalDetailsData"
  (visibleChange)="onApprovalDetailsDialogVisibleChange($event)"
  (approve)="onApprovalDetailsApprove($event)"
  (reject)="onApprovalDetailsReject($event)"
></app-approval-details-dialog>
